/**
 * Toast + ToastProvider.
 *
 * Used by: every mutation in all three apps. The house rule is "a mutation that
 * succeeds silently looks broken" — so a save shows a success toast and a
 * failure shows the API's Vietnamese message plus its error code.
 *
 * Accessibility: the region is `aria-live="polite"` for success/info and
 * `assertive` for errors, so a screen reader announces a failed save without
 * the user having to hunt for it. Toasts are dismissible by keyboard and the
 * auto-dismiss timer PAUSES on hover/focus — a five-second timer is not enough
 * to read a long error while using a screen reader.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cn } from '../utils.js';
import { TONE_CLASSES, type Tone } from '../status.js';
import { IconButton } from './IconButton.js';
import { CloseGlyph } from './Modal.js';

export type ToastTone = Extract<Tone, 'info' | 'success' | 'warning' | 'danger' | 'neutral'>;

export interface ToastOptions {
  readonly title: string;
  readonly description?: string;
  readonly tone?: ToastTone;
  /** ms; 0 keeps it until dismissed. Errors default to sticky-ish 8s. */
  readonly durationMs?: number;
  /** One optional action, e.g. "Thử lại". */
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

interface ToastEntry extends ToastOptions {
  readonly id: number;
  readonly tone: ToastTone;
  readonly durationMs: number;
}

export interface ToastApi {
  show: (options: ToastOptions) => number;
  success: (title: string, description?: string) => number;
  error: (title: string, description?: string) => number;
  warning: (title: string, description?: string) => number;
  info: (title: string, description?: string) => number;
  dismiss: (id: number) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let seq = 0;

export interface ToastProviderProps {
  readonly children: ReactNode;
  /** Cap so a loop of failing requests cannot bury the screen. */
  readonly max?: number;
}

export function ToastProvider({ children, max = 4 }: ToastProviderProps): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (options: ToastOptions): number => {
      seq += 1;
      const tone = options.tone ?? 'info';
      const entry: ToastEntry = {
        ...options,
        id: seq,
        tone,
        durationMs: options.durationMs ?? (tone === 'danger' ? 8000 : 4500),
      };
      setToasts((prev) => [...prev, entry].slice(-max));
      return entry.id;
    },
    [max],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, description) => show({ title, description, tone: 'success' }),
      error: (title, description) => show({ title, description, tone: 'danger' }),
      warning: (title, description) => show({ title, description, tone: 'warning' }),
      info: (title, description) => show({ title, description, tone: 'info' }),
      dismiss,
      dismissAll: () => setToasts([]),
    }),
    [dismiss, show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document === 'undefined'
        ? null
        : createPortal(
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 p-4 sm:bottom-auto sm:right-0 sm:top-0 sm:items-end"
              // Polite by default; the danger toast below sets assertive on itself.
              aria-live="polite"
              aria-atomic="false"
            >
              {toasts.map((t) => (
                <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
              ))}
            </div>,
            document.body,
          )}
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  readonly toast: ToastEntry;
  readonly onDismiss: (id: number) => void;
}): React.JSX.Element {
  const [paused, setPaused] = useState(false);
  const remaining = useRef(toast.durationMs);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    if (toast.durationMs === 0 || paused) return undefined;
    startedAt.current = Date.now();
    const timer = window.setTimeout(() => onDismiss(toast.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    };
  }, [onDismiss, paused, toast.durationMs, toast.id]);

  return (
    <div
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      aria-live={toast.tone === 'danger' ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cn(
        'pointer-events-auto w-full max-w-sm animate-slide-up rounded-lg bg-surface p-3 shadow-pop ring-1 ring-line',
        'flex items-start gap-3',
      )}
    >
      <span aria-hidden="true" className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', TONE_CLASSES[toast.tone].dot)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">{toast.title}</p>
        {toast.description !== undefined ? (
          <p className="mt-0.5 break-words text-sm text-fg-muted">{toast.description}</p>
        ) : null}
        {toast.action !== undefined ? (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            className="mt-2 text-sm font-medium text-brand-600 underline-offset-4 hover:underline"
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
      <IconButton label="Đóng thông báo" size="sm" icon={<CloseGlyph />} onClick={() => onDismiss(toast.id)} />
    </div>
  );
}

/**
 * Throws when used outside a ToastProvider. That is deliberate: a silent no-op
 * would mean a failed save shows nothing at all, which is worse than a crash
 * during development.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error('useToast phải được dùng bên trong <ToastProvider>.');
  return ctx;
}
